// src/pages/BulkCreatePage.js - UPDATED with complete color system
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { useNotifications } from "../contexts/NotificationContext";
import ColorPicker from "../components/ColorPicker"; // Added import

export default function BulkCreatePage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [user, setUser] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [validation, setValidation] = useState({});
  const [customColorsEnabled, setCustomColorsEnabled] = useState(false); // Added for color UI control
  
  // Form state for common details
  const [commonForm, setCommonForm] = useState({
    niche: "",
    service: "",
    phone: "",
    email: "",
    favicon_url: "",
    related_services: "",
    search_console_code: "",
    platform: "",
    account_id: "",
    // Added color fields
    primary_color: "#3B82F6",
    secondary_color: "#1F2937", 
    accent_color: "#10B981",
  });
  
  // Cities list state
  const [citiesText, setCitiesText] = useState("");
  const [parsedCities, setParsedCities] = useState([]);
  
  const navigate = useNavigate();
  const { success, error, loading: showLoading, updateNotification } = useNotifications();

  const totalSteps = 5; // Updated step count

  // Step definitions - UPDATED with correct step 4
  const steps = [
    { id: 1, title: "Common Details", description: "Business info used for all sites", icon: "🏢" },
    { id: 2, title: "Cities List", description: "Add cities for site creation", icon: "🏙️" },
    { id: 3, title: "Design Options", description: "Customize colors (Optional)", icon: "🎨" }, // MOVED to step 3
    { id: 4, title: "Platform Setup", description: "Choose deployment platform", icon: "🚀" }, // MOVED to step 4
    { id: 5, title: "Review & Create", description: "Confirm and start bulk creation", icon: "✨" } // MOVED to step 5
  ];

  // Get user on mount
  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
    };
    getUser();
  }, []);

  // Fetch accounts when platform changes
  useEffect(() => {
    async function fetchAccounts() {
      if (!commonForm.platform || !user) {
        setAccounts([]);
        return;
      }
      
      let data = [];
      if (commonForm.platform === "netlify") {
        const { data: net } = await supabase
          .from("netlify_tokens")
          .select("id, token_name")
          .eq("user_id", user.id);
        data = net || [];
      } else if (commonForm.platform === "aws") {
        const { data: aws } = await supabase
          .from("aws_credentials")
          .select("id, cred_name")
          .eq("user_id", user.id);
        data = aws || [];
      }
      setAccounts(data);
      setCommonForm(prev => ({ ...prev, account_id: "" }));
    }
    
    fetchAccounts();
  }, [commonForm.platform, user]);

  const handleCommonFormChange = (e) => {
    const { name, value } = e.target;
    const finalValue = name === 'state_code' ? value.toUpperCase() : value;
    setCommonForm(prev => ({ ...prev, [name]: finalValue }));
    
    if (validation[name]) {
      setValidation(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleCitiesTextChange = (e) => {
    const text = e.target.value;
    setCitiesText(text);
    
    // Parse cities in real-time
    const lines = text.split('\n').filter(line => line.trim());
    const cities = [];
    
    for (const line of lines) {
      const parts = line.split(',').map(part => part.trim());
      if (parts.length >= 4) {
        cities.push({
          city: parts[0],
          state_name: parts[1],
          state_code: parts[2].toUpperCase(),
          zipcode: parts[3]
        });
      }
    }
    
    setParsedCities(cities);
  };

  const validateStep = (step) => {
    const errors = {};
    
    switch (step) {
      case 1:
        if (!commonForm.niche.trim()) errors.niche = "Niche is required";
        if (!commonForm.service.trim()) errors.service = "Service is required";
        if (!commonForm.phone.trim()) errors.phone = "Phone is required";
        if (!commonForm.email.trim()) {
          errors.email = "Email is required";
        } else if (!/\S+@\S+\.\S+/.test(commonForm.email)) {
          errors.email = "Please enter a valid email address";
        }
        if (!commonForm.related_services.trim()) errors.related_services = "Related services are required";
        break;
      case 2:
        if (parsedCities.length === 0) {
          errors.cities = "At least one city is required";
        }
        break;
      case 3:
        // Color validation (only if custom colors are enabled)
        if (customColorsEnabled) {
          if (!commonForm.primary_color || !commonForm.primary_color.match(/^#[0-9A-F]{6}$/i)) {
            errors.primary_color = "Valid primary color is required";
          }
          if (!commonForm.accent_color || !commonForm.accent_color.match(/^#[0-9A-F]{6}$/i)) {
            errors.accent_color = "Valid accent color is required";
          }
        }
        break;
      case 4:
        if (!commonForm.platform) errors.platform = "Platform selection is required";
        if (!commonForm.account_id) errors.account_id = "Account selection is required";
        break;
    }
    
    setValidation(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const handlePrev = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) return;
    
    setLoading(true);
    const loadingId = showLoading(`Creating ${parsedCities.length} sites...`);
    
    try {
      const relatedServicesArray = commonForm.related_services
        .split("\n")
        .map(s => s.trim())
        .filter(s => s.length > 0);

      // UPDATED: Include color fields in bulk operation data
      const baseData = {
        ...commonForm,
        related_services: relatedServicesArray,
        // Include color fields only if custom colors are enabled
        ...(customColorsEnabled && {
          primary_color: commonForm.primary_color,
          secondary_color: commonForm.secondary_color,
          accent_color: commonForm.accent_color
        })
      };

      const bulkOperationData = {
        user_id: user.id,
        operation_type: 'bulk_create',
        total_sites: parsedCities.length,
        status: 'pending',
        base_data: baseData,
        cities_data: parsedCities
      };

      // Insert bulk operation record
      const { data: operation, error: insertError } = await supabase
        .from('bulk_operations')
        .insert([bulkOperationData])
        .select()
        .single();

      if (insertError) throw insertError;

      // Start the bulk creation process via API
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}/bulk-sites/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify({ operationId: operation.id })
      });

      if (!response.ok) {
        throw new Error('Failed to start bulk creation');
      }

      updateNotification(loadingId, {
        type: 'success',
        message: `Bulk creation started! Creating ${parsedCities.length} sites...`,
        duration: 5000
      });
      
      setTimeout(() => navigate("/dashboard"), 2000);
      
    } catch (err) {
      updateNotification(loadingId, {
        type: 'error',
        message: 'Bulk creation failed: ' + err.message,
        duration: 7000
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-xl border border-gray-200 p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Bulk Site Creation</h1>
          <p className="text-gray-600">Create multiple sites for different cities with the same business details</p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            {steps.map((step, index) => (
              <div key={step.id} className="flex flex-col items-center relative">
                {index < steps.length - 1 && (
                  <div className={`absolute top-4 left-8 w-full h-0.5 ${
                    currentStep > step.id ? 'bg-indigo-600' : 'bg-gray-200'
                  }`} style={{ width: 'calc(100% + 2rem)' }} />
                )}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold relative z-10 ${
                  currentStep > step.id 
                    ? 'bg-indigo-600 text-white' 
                    : currentStep === step.id
                      ? 'bg-indigo-100 text-indigo-600 ring-2 ring-indigo-600'
                      : 'bg-gray-200 text-gray-500'
                }`}>
                  {currentStep > step.id ? '✓' : step.id}
                </div>
                <div className="mt-2 text-center">
                  <p className={`text-xs font-medium ${
                    currentStep >= step.id ? 'text-gray-900' : 'text-gray-400'
                  }`}>
                    {step.title}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {steps[currentStep - 1].icon} {steps[currentStep - 1].title}
            </h2>
            <p className="text-gray-600">{steps[currentStep - 1].description}</p>
          </div>
        </div>
        
        {/* Step Content */}
        <div className="mt-8">
          {/* Step 1: Common Details */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="bg-blue-50 rounded-lg p-4 mb-6">
                <p className="text-blue-800 text-sm">
                  <strong>Note:</strong> These details will be used for all sites in your bulk creation.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Business Niche <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="niche"
                    value={commonForm.niche}
                    onChange={handleCommonFormChange}
                    placeholder="e.g. Home Services, Healthcare"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                      validation.niche ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                  />
                  {validation.niche && <p className="text-red-500 text-sm mt-1">{validation.niche}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Primary Service <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="service"
                    value={commonForm.service}
                    onChange={handleCommonFormChange}
                    placeholder="e.g. Water Damage Restoration"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                      validation.service ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                  />
                  {validation.service && <p className="text-red-500 text-sm mt-1">{validation.service}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Business Phone <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={commonForm.phone}
                    onChange={handleCommonFormChange}
                    placeholder="(555) 123-4567"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                      validation.phone ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                  />
                  {validation.phone && <p className="text-red-500 text-sm mt-1">{validation.phone}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Business Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={commonForm.email}
                    onChange={handleCommonFormChange}
                    placeholder="contact@yourbusiness.com"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                      validation.email ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                  />
                  {validation.email && <p className="text-red-500 text-sm mt-1">{validation.email}</p>}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Related Services <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="related_services"
                  value={commonForm.related_services}
                  onChange={handleCommonFormChange}
                  rows={4}
                  placeholder="List each service on a new line:&#10;Emergency Water Extraction&#10;Structural Drying&#10;Mold Remediation&#10;Insurance Claims Assistance"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                    validation.related_services ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                />
                <p className="text-xs text-gray-500 mt-1">Each service on a new line</p>
                {validation.related_services && <p className="text-red-500 text-sm mt-1">{validation.related_services}</p>}
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Favicon URL
                  </label>
                  <input
                    type="url"
                    name="favicon_url"
                    value={commonForm.favicon_url}
                    onChange={handleCommonFormChange}
                    placeholder="https://example.com/favicon.ico (optional)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Google Search Console Code
                  </label>
                  <input
                    type="text"
                    name="search_console_code"
                    value={commonForm.search_console_code}
                    onChange={handleCommonFormChange}
                    placeholder="google-site-verification=... (optional)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Cities List */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="bg-green-50 rounded-lg p-4">
                <h4 className="font-medium text-green-800 mb-2">City List Format</h4>
                <p className="text-green-700 text-sm mb-2">Paste your cities in this format (one per line):</p>
                <code className="bg-white px-2 py-1 rounded text-sm">
                  CityName, StateName, StateCode, ZipCode
                </code>
                <div className="mt-2 text-green-700 text-sm">
                  <strong>Example:</strong><br/>
                  Phoenix, Arizona, AZ, 85001<br/>
                  Dallas, Texas, TX, 75201<br/>
                  Miami, Florida, FL, 33101
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cities List <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={citiesText}
                  onChange={handleCitiesTextChange}
                  rows={10}
                  placeholder="Phoenix, Arizona, AZ, 85001&#10;Dallas, Texas, TX, 75201&#10;Miami, Florida, FL, 33101"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm ${
                    validation.cities ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                />
                {validation.cities && <p className="text-red-500 text-sm mt-1">{validation.cities}</p>}
              </div>

              {parsedCities.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-800 mb-3">
                    Parsed Cities ({parsedCities.length} sites will be created)
                  </h4>
                  <div className="max-h-48 overflow-y-auto">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {parsedCities.map((city, index) => (
                        <div key={index} className="bg-white rounded p-2 text-sm">
                          <span className="font-medium">{city.city}</span>, {city.state_name} ({city.state_code}) - {city.zipcode}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Design Options - ADDED complete color system */}
          {currentStep === 3 && (
            <div className="space-y-6">
              {/* Enable Custom Colors Toggle */}
              <div className="bg-blue-50 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-blue-800 mb-1">🎨 Customize Site Colors</h4>
                    <p className="text-blue-700 text-sm">
                      Apply the same custom brand colors to all {parsedCities.length} sites, or keep the professional blue theme.
                    </p>
                  </div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={customColorsEnabled}
                      onChange={(e) => {
                        setCustomColorsEnabled(e.target.checked);
                        // Reset to defaults if unchecked
                        if (!e.target.checked) {
                          setCommonForm(prev => ({ 
                            ...prev, 
                            primary_color: "#3B82F6",
                            secondary_color: "#1F2937",
                            accent_color: "#10B981"
                          }));
                        }
                      }}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 mr-2"
                    />
                    <span className="text-sm font-medium text-blue-800">Customize Colors</span>
                  </label>
                </div>
              </div>

              {/* Color Customization Options (only show if enabled) */}
              {customColorsEnabled ? (
                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Primary Color */}
                    <ColorPicker
                      label="Primary Color (Headers, Buttons)"
                      value={commonForm.primary_color}
                      onChange={(color) => handleCommonFormChange({ target: { name: 'primary_color', value: color } })}
                    />

                    {/* Accent Color */}
                    <ColorPicker
                      label="Accent Color (Links, Highlights)"
                      value={commonForm.accent_color}
                      onChange={(color) => handleCommonFormChange({ target: { name: 'accent_color', value: color } })}
                      presetColors={['#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#F97316']}
                    />
                  </div>

                  {/* Live Preview */}
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h4 className="font-medium text-gray-800 mb-3">Preview (All {parsedCities.length} sites will use these colors)</h4>
                    <div 
                      className="bg-white rounded-lg p-6 border shadow-sm"
                      style={{ 
                        borderColor: commonForm.primary_color + '20'
                      }}
                    >
                      <h3 
                        className="text-xl font-bold mb-2"
                        style={{ color: commonForm.primary_color }}
                      >
                        {commonForm.service || 'Your Service'} in Multiple Cities
                      </h3>
                      <p className="text-gray-600 mb-4">
                        Professional {(commonForm.service || 'service').toLowerCase()} services across {parsedCities.length} locations.
                      </p>
                      <button 
                        className="px-4 py-2 rounded-lg text-white font-medium mr-3"
                        style={{ backgroundColor: commonForm.primary_color }}
                      >
                        Contact Us
                      </button>
                      <span 
                        className="font-medium"
                        style={{ color: commonForm.accent_color }}
                      >
                        Call {commonForm.phone || '(555) 123-4567'}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                /* Default Blue Theme Preview */
                <div className="space-y-6">
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h4 className="font-medium text-gray-800 mb-3">Default Professional Theme</h4>
                    <div className="bg-white rounded-lg p-6 border shadow-sm border-blue-100">
                      <h3 className="text-xl font-bold mb-2 text-blue-600">
                        {commonForm.service || 'Your Service'} in Multiple Cities
                      </h3>
                      <p className="text-gray-600 mb-4">
                        Professional {(commonForm.service || 'service').toLowerCase()} services across {parsedCities.length} locations.
                      </p>
                      <button className="px-4 py-2 rounded-lg text-white font-medium mr-3 bg-blue-600">
                        Contact Us
                      </button>
                      <span className="font-medium text-emerald-500">
                        Call {commonForm.phone || '(555) 123-4567'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-3 text-center">
                      Professional blue theme - trusted and reliable
                    </p>
                  </div>
                </div>
              )}

              {/* Skip Option */}
              <div className="text-center">
                <button
                  type="button"
                  onClick={handleNext}
                  className="text-gray-500 hover:text-gray-700 text-sm underline"
                >
                  Skip color customization and use default theme
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Platform Setup (moved from step 3) */}
          {currentStep === 4 && (
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                {/* Netlify Option */}
                <div
                  onClick={() => handleCommonFormChange({ target: { name: 'platform', value: 'netlify' } })}
                  className={`relative border-2 rounded-xl p-6 cursor-pointer transition-all ${
                    commonForm.platform === 'netlify'
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gray-900 rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold text-sm">N</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">Netlify</h3>
                        <p className="text-sm text-gray-500">Easy deployment</p>
                      </div>
                    </div>
                    <input
                      type="radio"
                      name="platform"
                      value="netlify"
                      checked={commonForm.platform === 'netlify'}
                      onChange={handleCommonFormChange}
                      className="text-indigo-600"
                    />
                  </div>
                </div>

                {/* AWS Option */}
                <div
                  onClick={() => handleCommonFormChange({ target: { name: 'platform', value: 'aws' } })}
                  className={`relative border-2 rounded-xl p-6 cursor-pointer transition-all ${
                    commonForm.platform === 'aws'
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold text-sm">AWS</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">AWS S3</h3>
                        <p className="text-sm text-gray-500">Enterprise grade</p>
                      </div>
                    </div>
                    <input
                      type="radio"
                      name="platform"
                      value="aws"
                      checked={commonForm.platform === 'aws'}
                      onChange={handleCommonFormChange}
                      className="text-indigo-600"
                    />
                  </div>
                </div>
              </div>
              
              {validation.platform && <p className="text-red-500 text-sm">{validation.platform}</p>}

              {/* Account Selection */}
              {commonForm.platform && (
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select {commonForm.platform === 'netlify' ? 'Netlify' : 'AWS'} Account *
                  </label>
                  {accounts.length === 0 ? (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                      <p className="text-gray-500 mb-4">
                        No {commonForm.platform === 'netlify' ? 'Netlify tokens' : 'AWS credentials'} found
                      </p>
                      <button
                        type="button"
                        onClick={() => navigate('/settings')}
                        className="text-indigo-600 hover:text-indigo-700 font-medium"
                      >
                        Add {commonForm.platform === 'netlify' ? 'Netlify Token' : 'AWS Credentials'} in Settings
                      </button>
                    </div>
                  ) : (
                    <select
                      name="account_id"
                      value={commonForm.account_id}
                      onChange={handleCommonFormChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                        validation.account_id ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Choose account</option>
                      {accounts.map(acc => (
                        <option key={acc.id} value={acc.id}>
                          {commonForm.platform === "netlify" ? acc.token_name : acc.cred_name}
                        </option>
                      ))}
                    </select>
                  )}
                  {validation.account_id && <p className="text-red-500 text-sm mt-1">{validation.account_id}</p>}
                </div>
              )}
            </div>
          )}

          {/* Step 5: Review (moved from step 4) */}
          {currentStep === 5 && (
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="font-semibold text-lg mb-4">Bulk Creation Summary</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Common Business Info</h4>
                    <div className="space-y-2 text-sm">
                      <p><span className="text-gray-500">Service:</span> {commonForm.service}</p>
                      <p><span className="text-gray-500">Niche:</span> {commonForm.niche}</p>
                      <p><span className="text-gray-500">Phone:</span> {commonForm.phone}</p>
                      <p><span className="text-gray-500">Email:</span> {commonForm.email}</p>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Deployment Info</h4>
                    <div className="space-y-2 text-sm">
                      <p><span className="text-gray-500">Platform:</span> {commonForm.platform?.toUpperCase()}</p>
                      <p><span className="text-gray-500">Cities:</span> {parsedCities.length} locations</p>
                      <p><span className="text-gray-500">Total Sites:</span> {parsedCities.length}</p>
                      {customColorsEnabled && (
                        <>
                          <p><span className="text-gray-500">Colors:</span> Custom theme</p>
                          <div className="flex space-x-2 mt-1">
                            <div className="w-4 h-4 rounded border" style={{ backgroundColor: commonForm.primary_color }} title="Primary"></div>
                            <div className="w-4 h-4 rounded border" style={{ backgroundColor: commonForm.accent_color }} title="Accent"></div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 rounded-xl p-6">
                <h3 className="font-semibold text-lg mb-2">🚀 What happens next?</h3>
                <div className="space-y-2 text-sm text-gray-700">
                  <p>• AI will generate unique content for each city location</p>
                  <p>• {parsedCities.length} static sites will be created automatically</p>
                  {customColorsEnabled && <p>• Your custom colors will be applied to all sites</p>}
                  <p>• Sites will be ready for manual deployment when complete</p>
                  <p>• You can track progress on the dashboard</p>
                  <p>• Estimated completion time: {Math.ceil(parsedCities.length / 5) * 3} minutes</p>
                </div>
              </div>

              {parsedCities.length > 0 && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="font-medium text-gray-800 mb-3">
                    Cities to Create ({parsedCities.length} sites)
                  </h4>
                  <div className="max-h-32 overflow-y-auto">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                      {parsedCities.map((city, index) => (
                        <div key={index} className="bg-white rounded p-2">
                          {city.city}, {city.state_code}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={handlePrev}
            disabled={currentStep === 1}
            className={`px-6 py-2 rounded-lg font-medium transition-colors ${
              currentStep === 1
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            ← Previous
          </button>

          <div className="text-sm text-gray-500">
            Step {currentStep} of {totalSteps}
          </div>

          {currentStep < totalSteps ? (
            <button
              type="button"
              onClick={handleNext}
              className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors"
            >
              Next →
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="px-8 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Creating Sites...</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <span>Create {parsedCities.length} Sites</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}